import { createContext, useCallback, useEffect, useMemo, useReducer, useRef } from 'react';
import { DEFAULT_APP_NAME } from '../../constants/general';
import { PAGE_VISIBILITY_CHECK_TIMEOUT } from '../../constants/timeouts';
import { loggingMiddleware } from '../../utils/store';
import { postponeWithAppLoader } from './functions';
import { initialState, POSTPONE_ACTION, reducer, RESET_APP_STATE, SET_IS_IN_MEETING, SET_IS_PAGE_HIDDEN, SET_IS_PAST_MEETING, SET_SCOPES, SET_SKIP_REFRESH, SET_STORE_BEFORE_INIT } from './reducer';
import { jsx as _jsx } from "react/jsx-runtime";
export const AppContext = /*#__PURE__*/createContext(initialState);

// this variable being used in case if we trigger actions
// before context is initialized
let storeBeforeInit = Object.assign({}, initialState);
let appDispatch = arg => {
  if (arg.type === SET_IS_IN_MEETING) {
    storeBeforeInit = Object.assign({}, storeBeforeInit, {
      isInMeeting: arg.payload
    });
  }
  if (arg.type === SET_IS_PAST_MEETING) {
    storeBeforeInit = Object.assign({}, storeBeforeInit, {
      isPastMeeting: arg.payload
    });
  }
  if (arg.type === RESET_APP_STATE) {
    storeBeforeInit = Object.assign({}, initialState, {
      appName: storeBeforeInit.appName
    });
  }
};
export const appActions = {
  postponeWithAppLoader: fn => appDispatch({
    type: POSTPONE_ACTION,
    payload: fn
  }),
  setSkipIntervalRefresh: value => appDispatch({
    type: SET_SKIP_REFRESH,
    payload: value
  }),
  setIsInMeeting: value => appDispatch({
    type: SET_IS_IN_MEETING,
    payload: value
  }),
  setIsPastMeeting: value => appDispatch({
    type: SET_IS_PAST_MEETING,
    payload: value
  }),
  resetAppState: () => appDispatch({
    type: RESET_APP_STATE
  }),
  setScopes: scopes => appDispatch({
    type: SET_SCOPES,
    payload: scopes
  })
};
export const AppContextProvider = ({
  children,
  appName = DEFAULT_APP_NAME,
  isInMeeting
}) => {
  storeBeforeInit.appName = appName;
  storeBeforeInit.isInMeeting = isInMeeting !== null && isInMeeting !== void 0 ? isInMeeting : storeBeforeInit.isInMeeting;
  const [app, dispatchCTX] = useReducer(loggingMiddleware('app')(reducer), Object.assign({}, initialState, {
    appName
  }));
  const intervalIdRef = useRef(undefined);
  const asyncFuncArg = useMemo(() => ({
    dispatch: dispatchCTX
  }), []);
  const dispatch = useCallback(action => {
    switch (action.type) {
      case POSTPONE_ACTION:
        postponeWithAppLoader(asyncFuncArg, action.payload);
        break;
      default:
        {
          dispatchCTX(action);
        }
    }
  }, [asyncFuncArg]);
  appDispatch = dispatch;
  const selectors = useMemo(() => ({
    isDraft: !app.isPastMeeting && !app.isInMeeting,
    isInLiveMeeting: !!app.isInMeeting && !app.isPastMeeting,
    isInLivePastMeeting: !!app.isInMeeting && app.isPastMeeting,
    isNotInLiveMeeting: app.isInMeeting === false || app.isPastMeeting
  }), [app]);
  const state = useMemo(() => Object.assign({}, app, {
    dispatch,
    selectors
  }), [app, dispatch, selectors]);
  const visibilityChangeHandler = useCallback(() => {
    if (app.isPageHidden !== document.hidden) {
      dispatch({
        type: SET_IS_PAGE_HIDDEN,
        payload: document.hidden
      });
    }
  }, [dispatch, app.isPageHidden]);

  // set storeBeforeInit to keep changes which
  // have been done before context initialization
  useEffect(() => {
    dispatch({
      type: SET_STORE_BEFORE_INIT,
      payload: storeBeforeInit
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    intervalIdRef.current = setInterval(visibilityChangeHandler, PAGE_VISIBILITY_CHECK_TIMEOUT);
    return () => clearInterval(intervalIdRef.current);
  }, [visibilityChangeHandler]);
  return /*#__PURE__*/_jsx(AppContext.Provider, {
    value: state,
    children: children
  });
};